// Number of seat positions in a full hall row (seat 1 is the rightmost position).
export const ROW_WIDTH = 18;

// Index in the 18-position row (counted from the left) before which the row number is drawn.
export const ROW_NUMBER_INDEX = 9;

/**
 * Returns the seat key drawn at each of the ROW_WIDTH positions of a row, leftmost first.
 * `null` means an empty gap.
 *
 * - A shorter row numbered 1..N without gaps (e.g. rows 14-15 of Babajanyan Hall: 13 seats)
 *   is split like the real hall: seats 1..floor(N/2) on the right block, the rest continue
 *   from the far left (13 seats -> 1-6 on the right, 7-13 on the left).
 * - Any other row is positional: seat N sits at position N, so all 18 keys are returned
 *   and the caller decides how to draw keys that are missing from the data.
 *
 * Keep in sync with rowLayout() in the WordPress theme (template-parts/seat-selection.php).
 */
export function getRowLayout(columns: Record<string, unknown> | undefined): (string | null)[] {
  const seatNumbers = Object.keys(columns || {})
    .map(Number)
    .filter((n) => Number.isInteger(n) && n > 0)
    .sort((a, b) => a - b);
  const count = seatNumbers.length;
  const positions: (string | null)[] = Array(ROW_WIDTH).fill(null);

  if (count > 0 && count < ROW_WIDTH && seatNumbers[count - 1] === count) {
    const rightBlockSize = Math.floor(count / 2);
    seatNumbers.forEach((seat) => {
      const position = seat <= rightBlockSize ? seat : ROW_WIDTH - (count - seat);
      positions[ROW_WIDTH - position] = seat.toString();
    });
  } else {
    for (let position = 1; position <= ROW_WIDTH; position++) {
      positions[ROW_WIDTH - position] = position.toString();
    }
  }

  return positions;
}
